import { Fragment, PropsWithChildren } from "react";

import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import { media } from "@atlaskit/primitives";

import { PlanFeatureMessage } from "../constants";

import { PlanFeature } from "./price-card-plan-feature";

const baseBorderStyles = css({
    borderInlineStartWidth: "1px",
    borderInlineEndWidth: "1px",
    borderBlockStartWidth: "0px",
    borderBlockEndWidth: "0px",
    borderStyle: "solid"
});

const recommendedLabelStyle = css({
    backgroundColor: "#091E42",
    color: "white",
    gridRow: "1",
    fontSize: "0.75rem",
    fontStyle: "normal",
    fontFamily: "var(--bxp-charlie-display-font-family)",
    fontWeight: "500",
    lineHeight: "21px",
    textAlign: "center",
    letterSpacing: "1px",
    height: "100%"
});

const planNameContainerStyles = css({
    fontSize: "16px",
    gridRow: "2",
    lineHeight: "24px",
    textAlign: "center"
});
const planNameStyles = css({
    fontSize: "24px",
    fontWeight: "700",
    lineHeight: "32px",
    margin: "0 0 8px",
    padding: "0px",
    color: "#172B4D"
});

const descriptionStyles = css({
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: "400",
    lineHeight: "18px",
    color: "#172B4D"
});

const ctaButtonStyles = css({
    marginBlockEnd: "32px"
});

const priceCardBodyContainerStyles = css({
    background: "white",
    display: "flex",
    flexDirection: "column",
    gridRow: "3",
    padding: "0 20px",
    textAlign: "center",
    borderBlockEndWidth: "1px !important"
});

const featureMessagesContainerStyles = css({
    "background": "white",
    "gridRow": "4",
    "lineHeight": "24px",
    "paddingBlockEnd": "20px",
    "paddingInlineEnd": "20px",
    "marginBlockEnd": "25px",
    "@media (min-width: 1024px)": { marginBlockEnd: "0px" }
});
const featureMessageTitleStyles = css({
    fontWeight: "500",
    padding: "16px 0 0 20px"
});

type PriceCardRedesignProps = {
    first?: boolean;
    last?: boolean;
    highlighted: boolean;
    planName: string;
    description?: string;
    featureMessagePlanName?: string;
    planFeatureMessages?: PlanFeatureMessage[];
    ctaButton: React.ReactNode;
};

// eslint-disable-next-line complexity
const PriceCardRedesign = ({
    first = false,
    last = false,
    highlighted = false,
    planName,
    description,
    ctaButton,
    featureMessagePlanName,
    planFeatureMessages,
    children
}: PropsWithChildren<PriceCardRedesignProps>) => {
    return (
        <Fragment>
            <div
                css={[
                    baseBorderStyles,
                    planNameContainerStyles,
                    css({
                        "borderColor": highlighted ? "#091E42" : "#dfe1e5",
                        "padding": highlighted ? "0px" : "20px 25px 0px 25px",
                        "borderBlockStart": highlighted
                            ? `2px solid #091E42`
                            : `1px solid #dfe1e5`,
                        "borderStartStartRadius": "8px",
                        "borderStartEndRadius": "8px",
                        "@media (min-width: 1024px)": {
                            borderStartStartRadius: first ? "8px" : "0px",
                            borderStartEndRadius: last ? "8px" : "0px"
                        }
                    })
                ]}
            >
                <div
                    css={css({
                        paddingBlockEnd: "20px"
                    })}
                >
                    {highlighted && (
                        <div
                            css={[
                                recommendedLabelStyle,
                                css({
                                    borderStartStartRadius: "4px",
                                    borderStartEndRadius: "4px",
                                    paddingBlockEnd: "2px",
                                    [media.above.md]: {
                                        borderStartStartRadius: first
                                            ? "4px"
                                            : "0px",
                                        borderStartEndRadius: last
                                            ? "4px"
                                            : "0px"
                                    }
                                })
                            ]}
                        >
                            <FormattedMessage
                                id="Pricing.PriceCard.recommendedText"
                                defaultMessage="RECOMMENDED"
                                description="Recommneded text for highlighted plan"
                            />
                        </div>
                    )}
                </div>
                <div
                    css={css({
                        padding: "0px 25px 0px 25px"
                    })}
                >
                    <h3 css={planNameStyles}>{planName}</h3>

                    <div css={descriptionStyles}>{description}</div>
                </div>
            </div>

            <div
                css={[
                    baseBorderStyles,
                    priceCardBodyContainerStyles,
                    css({
                        borderColor: highlighted ? "#091E42" : "#dfe1e5",
                        borderBlockEnd: "1px solid #dfe1e5 !important",
                        borderEndStartRadius: !planFeatureMessages?.length
                            ? "8px"
                            : "0px",
                        borderEndEndRadius: !planFeatureMessages?.length
                            ? "8px"
                            : "0px",
                        marginBlockEnd: !planFeatureMessages?.length
                            ? "25px"
                            : "0px",
                        [media.above.md]: {
                            borderEndStartRadius:
                                !planFeatureMessages?.length && first
                                    ? "8px"
                                    : "0px",
                            borderEndEndRadius:
                                !planFeatureMessages?.length && last
                                    ? "8px"
                                    : "0px"
                        }
                    })
                ]}
            >
                {children}
                <div css={ctaButtonStyles}>{ctaButton}</div>
            </div>
            {planFeatureMessages && planFeatureMessages.length > 0 && (
                <div
                    css={[
                        featureMessagesContainerStyles,
                        baseBorderStyles,
                        css({
                            borderColor: highlighted ? "#091E42" : "#dfe1e5",
                            borderBlockEnd: highlighted
                                ? `2px solid #091E42`
                                : `1px solid #dfe1e5`,
                            borderEndStartRadius: "8px",
                            borderEndEndRadius: "8px",
                            [media.above.md]: {
                                borderEndStartRadius: first ? "8px" : "0px",
                                borderEndEndRadius: last ? "8px" : "0px"
                            }
                        })
                    ]}
                >
                    {!featureMessagePlanName && (
                        <div css={featureMessageTitleStyles}>
                            <FormattedMessage
                                id="Pricing.PriceCard.featureMessageTitleFree"
                                defaultMessage="Includes:"
                            />
                        </div>
                    )}
                    {featureMessagePlanName && (
                        <div css={featureMessageTitleStyles}>
                            <FormattedMessage
                                id="Pricing.PriceCard.featureMessageTitle"
                                defaultMessage="Everything from {featureMessagePlanName} plus:"
                                values={{ featureMessagePlanName }}
                            />
                        </div>
                    )}
                    {planFeatureMessages.map((planFeatureMessage, idx) => (
                        <PlanFeature key={idx} {...planFeatureMessage} />
                    ))}
                </div>
            )}
        </Fragment>
    );
};

export default PriceCardRedesign;
